import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Truck, Home as HomeIcon, ArrowRight, ShieldCheck, Clock, MessageCircle, Bike, Handshake, User, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import Header from "../components/Header";
import Reviews from "../components/Reviews";

const HERO_IMG =
  "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxtb3ZpbmclMjB0cnVjayUyMGZyZWlnaHQlMjBjYXJnbyUyMGRlbGl2ZXJ5JTIwc2VydmljZXxlbnwwfHx8fDE3ODg1Mjg5MTJ8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const navigate = useNavigate();
  const [pub, setPub] = useState(null);
  useEffect(() => { api.get("/public/settings").then((r) => setPub(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-[#0b1220] flex justify-center">
      <div className="w-full max-w-md mx-auto min-h-screen bg-slate-50 shadow-2xl relative" data-testid="home-page">
        <Header />

        <section className="relative bg-navy overflow-hidden">
          <img
            src={HERO_IMG}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/80 to-navy" />
          <div className="relative px-5 pt-8 pb-10">
            <img src="/logo.jpg" alt="Carlos Jr Fretes e Mudanças" className="h-24 w-auto object-contain mx-auto mb-5 rounded-xl bg-white p-1.5" data-testid="hero-logo" />
            <h1 className="font-heading text-3xl font-extrabold text-white text-center tracking-tight">
              Fretes e Mudanças
            </h1>
            <p className="text-slate-300 text-sm text-center mt-3 leading-relaxed max-w-xs mx-auto">
              Seu frete ou mudança de forma rápida, prática e segura. Informe os detalhes do serviço e receba uma estimativa do seu orçamento.
            </p>

            <div className="grid grid-cols-2 gap-3 mt-7">
              <button
                data-testid="frete-option-card"
                onClick={() => navigate("/orcamento?tipo=frete")}
                className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-4 text-left active:scale-95 transition-transform hover:border-[#F5B301]/60"
              >
                <Truck className="w-8 h-8 text-[#F5B301] mb-2" />
                <p className="font-heading font-bold text-white text-base">FRETE</p>
                <p className="text-[11px] text-slate-300 mt-1 leading-snug">Transporte de um item ou poucos itens</p>
              </button>
              <button
                data-testid="mudanca-option-card"
                onClick={() => navigate("/orcamento?tipo=mudanca")}
                className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-4 text-left active:scale-95 transition-transform hover:border-[#F5B301]/60"
              >
                <HomeIcon className="w-8 h-8 text-[#F5B301] mb-2" />
                <p className="font-heading font-bold text-white text-base">MUDANÇA</p>
                <p className="text-[11px] text-slate-300 mt-1 leading-snug">Móveis, caixas e objetos da casa toda</p>
              </button>
            </div>

            <button
              data-testid="entrega-option-card"
              onClick={() => {
                if (pub?.entrega_ativa) navigate("/orcamento?tipo=entrega");
                else toast.info("ENTREGA — entrará em funcionamento em breve.");
              }}
              className={`mt-3 w-full rounded-2xl p-4 text-left active:scale-95 transition-transform flex items-center gap-3 border ${
                pub?.entrega_ativa ? "bg-white/10 backdrop-blur-md border-white/15 hover:border-[#F5B301]/60" : "bg-white/5 border-white/10 opacity-70"}`}
            >
              <Bike className="w-8 h-8 text-[#F5B301] shrink-0" />
              <div>
                <p className="font-heading font-bold text-white text-base">ENTREGA</p>
                <p className="text-[11px] text-slate-300 mt-0.5 leading-snug">
                  {pub?.entrega_ativa ? "Entregas rápidas e seguras" : "Entrará em funcionamento em breve"}
                </p>
              </div>
              {!pub?.entrega_ativa && (
                <span className="ml-auto text-[9px] font-bold bg-slate-700 text-slate-300 px-2 py-1 rounded-full" data-testid="entrega-em-breve">EM BREVE</span>
              )}
            </button>

            <button
              data-testid="calculate-budget-btn"
              onClick={() => navigate("/orcamento")}
              className="mt-5 w-full py-4 rounded-2xl bg-[#F5B301] text-navy font-heading font-extrabold text-base flex items-center justify-center gap-2 active:scale-95 transition-transform shadow-[0_8px_30px_rgba(245,179,1,0.35)]"
            >
              CALCULAR MEU ORÇAMENTO
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </section>

        <section className="px-4 py-6" data-testid="acesso-section">
          <p className="text-xs font-bold tracking-wider uppercase text-slate-400 text-center mb-3">Acessar</p>
          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="minha-conta-btn"
              onClick={() => navigate("/minha-conta")}
              className="bg-white rounded-2xl border-2 border-navy p-4 text-center active:scale-95 transition-transform"
            >
              <User className="w-7 h-7 text-navy mx-auto mb-1" />
              <p className="font-heading font-bold text-navy text-xs">MINHA CONTA</p>
            </button>
            <button
              data-testid="consultar-agendamento-btn"
              onClick={() => navigate("/meus-servicos")}
              className="bg-white rounded-2xl border-2 border-navy p-4 text-center active:scale-95 transition-transform"
            >
              <CalendarDays className="w-7 h-7 text-navy mx-auto mb-1" />
              <p className="font-heading font-bold text-navy text-xs">CONSULTAR AGENDAMENTO</p>
            </button>
          </div>
          <button
            data-testid="entrar-motorista-btn"
            onClick={() => navigate("/motorista")}
            className="mt-2.5 w-full bg-navy rounded-2xl border-2 border-navy p-4 text-center active:scale-95 transition-transform"
          >
            <img src="/caminhao-parceiro.png" alt="" className="h-8 w-auto object-contain mx-auto mb-1" />
            <p className="font-heading font-bold text-[#F5B301] text-xs">ENTRAR COMO MOTORISTA / PRESTADOR</p>
          </button>
          <button
            data-testid="cadastrar-motorista-btn"
            onClick={() => navigate("/motorista?cadastro=1")}
            className="mt-2.5 w-full py-3.5 rounded-xl border-2 border-dashed border-[#F5B301] text-navy font-heading font-bold text-xs active:scale-95 transition-transform bg-gold-light/50"
          >
            CADASTRAR-SE COMO MOTORISTA / PRESTADOR
          </button>
        </section>

        <section className="px-4 pb-2" data-testid="clube-indicadores-section">
          <div className="bg-navy rounded-2xl p-5 text-center shadow-lg">
            <Handshake className="w-8 h-8 text-[#F5B301] mx-auto mb-2" />
            <h2 className="font-heading font-extrabold text-white text-base tracking-wide">CLUBE DE INDICADORES</h2>
            <p className="text-[11px] text-slate-300 mt-1.5 leading-relaxed">
              Indique clientes para a Carlos Júnior Fretes e Mudanças, acompanhe seus resultados e receba comissões por serviços elegíveis concluídos e pagos.
            </p>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <button
                data-testid="clube-cadastrar-home-btn"
                onClick={() => navigate("/clube/cadastro")}
                className="py-3 rounded-xl bg-[#F5B301] text-navy font-heading font-bold text-[11px] active:scale-95 transition-transform"
              >
                QUERO ME CADASTRAR
              </button>
              <button
                data-testid="clube-entrar-home-btn"
                onClick={() => navigate("/clube/login")}
                className="py-3 rounded-xl border-2 border-white/30 text-white font-heading font-bold text-[11px] active:scale-95 transition-transform"
              >
                JÁ SOU INDICADOR
              </button>
            </div>
          </div>
        </section>

        <section className="px-4 py-6 grid grid-cols-3 gap-3">
          {[
            { icon: ShieldCheck, label: "Seguro e confiável" },
            { icon: Clock, label: "Orçamento rápido" },
            { icon: MessageCircle, label: "Fecha pelo WhatsApp" },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="bg-white rounded-2xl p-3 border border-slate-100 text-center shadow-sm">
              <Icon className="w-6 h-6 text-navy mx-auto mb-1.5" />
              <p className="text-[11px] font-semibold text-slate-600 leading-tight">{label}</p>
            </div>
          ))}
        </section>

        <Reviews />

        <footer className="px-4 pb-10 pt-2 text-center space-y-2">
          <a href="/meus-servicos" data-testid="meus-servicos-link" className="inline-block text-xs font-bold text-navy underline">
            🔎 CONSULTAR AGENDAMENTO — acompanhe seus serviços
          </a>
          <p className="text-[11px] text-slate-400">
            Carlos Jr Fretes e Mudanças · Informou, calculou, recebeu, contratou.
          </p>
          <p className="text-[11px] font-bold text-navy" data-testid="footer-domain">carlosjrfretesemudancas.com.br</p>
        </footer>
      </div>
    </div>
  );
}
